import { FileText, MapPin } from 'lucide-react'
import { Card, CardContent } from './ui/card'

export interface TransactionItem {
  id: string
  name: string
  amount: number
  category?: string
  purchasedAt?: string
  merchant?: string
}

export interface TransactionData {
  id: string
  merchant?: string | null
  purchasedAt: string
  total: number
  receiptUrl?: string | null
  receiptFilename?: string | null
  receiptContentType?: string | null
  receiptSize?: number | null
  latitude?: number | null
  longitude?: number | null
  notes?: string | null
  items: TransactionItem[]
}

interface TransactionCardProps {
  transaction: TransactionData
  currencySymbol?: string
  onClick: () => void
}

function formatDate(purchasedAt: string) {
  const [y, m, d] = purchasedAt.split('T')[0].split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export function TransactionCard({ transaction, currencySymbol = '$', onClick }: TransactionCardProps) {
  const items = transaction.items ?? []
  const itemNames = items.map((i) => i.name)
  let summary = ''
  if (itemNames.length === 0) summary = 'No items'
  else if (itemNames.length <= 2) summary = itemNames.join(' · ')
  else summary = `${itemNames.slice(0, 2).join(' · ')} +${itemNames.length - 2} more`

  return (
    <Card
      className="overflow-hidden cursor-pointer hover:bg-muted/30 transition-colors"
      onClick={onClick}
    >
      <CardContent className="flex items-center justify-between p-3 gap-4">
        <div className="flex-grow min-w-0">
          <p className="font-medium truncate">{transaction.merchant ?? 'Purchase'}</p>
          <p className="text-xs text-muted-foreground truncate mt-0.5">{summary}</p>
          <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
            <span>{formatDate(transaction.purchasedAt)}</span>
            <span>·</span>
            <span>{items.length} {items.length === 1 ? 'item' : 'items'}</span>
            {transaction.latitude && transaction.longitude && (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${transaction.latitude},${transaction.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary inline-flex items-center"
                onClick={(e) => e.stopPropagation()}
              >
                <MapPin className="h-3 w-3" />
              </a>
            )}
            {transaction.receiptUrl && (
              <a
                href={transaction.receiptUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary inline-flex items-center"
                onClick={(e) => e.stopPropagation()}
              >
                <FileText className="h-3 w-3" />
              </a>
            )}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <span
            className="font-semibold tabular-nums"
            style={{ color: transaction.total < 0 ? '#16a34a' : undefined }}
          >
            {currencySymbol}{Math.abs(transaction.total).toFixed(2)}
          </span>
          {transaction.total < 0 && (
            <p className="text-xs text-green-600">refund</p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
